<script setup lang="ts">
import { ChevronDown } from 'lucide-vue-next'
import useContentScope from '@/composables/contentScope'
import type { ContentScope, ContentScopeSubject } from '@/types/contentScope'

const props = withDefaults(
  defineProps<{
    subject: ContentScopeSubject
    editable?: boolean
    save?: (scope: ContentScope) => Promise<unknown>
  }>(),
  {
    editable: true,
    save: undefined
  }
)

const scope = defineModel<ContentScope | null | undefined>('scope')

const { t } = useI18n()
const { canChooseScope } = useContentScope()

const isOpen = ref(false)
const isSaving = ref(false)

const canEdit = computed(() => props.editable && canChooseScope.value)

const confirm = async (value: ContentScope) => {
  if (value === scope.value) {
    isOpen.value = false
    return
  }
  if (!props.save) {
    scope.value = value
    isOpen.value = false
    return
  }
  isSaving.value = true
  const isSaved = await props.save(value).then(
    () => true,
    () => false
  )
  isSaving.value = false
  if (isSaved) isOpen.value = false
}
</script>

<template>
  <template v-if="canEdit">
    <ContentScopeBadge
      :scope
      as="button"
      type="button"
      class="cursor-pointer hover:brightness-95"
      aria-haspopup="dialog"
      data-testid="content-scope-detail-badge"
      @click="isOpen = true"
    >
      <span class="sr-only">{{ t('contentScope.field.edit') }}</span>
      <ChevronDown aria-hidden="true" />
    </ContentScopeBadge>
    <ContentScopeModal
      v-model:open="isOpen"
      :subject
      :scope="scope ?? null"
      :confirm-label="t('global.words.save')"
      :is-processing="isSaving"
      @confirm="confirm"
      @cancel="isOpen = false"
    />
  </template>
  <AppTooltip v-else-if="!canChooseScope" :text="t('contentScope.field.readOnly')">
    <ContentScopeBadge :scope />
  </AppTooltip>
  <ContentScopeBadge v-else :scope />
</template>
